/**
 * Table component for the GraphQL-backed Tasks Report macro.
 *
 * Differences from the legacy TasksReportTable:
 * - Renders TaskRow rows (ADF body, ISO dates, GraphQL shape).
 * - Uses bottom page-index controls (Prev/Next + page links) while still
 *   fetching additional cursor pages on demand (AGG has no totalCount field).
 * - Column sort keys are mapped to AGG InlineTasksQuerySortColumn values.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/button';
import DynamicTableStateless from '@atlaskit/dynamic-table/stateless';
import type { HeadType, RowType, SortOrderType } from '@atlaskit/dynamic-table/types';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';

import type { TaskEntity } from '../hooks/useTasksReportData.ts';
import type { TasksReportColumn } from '../hooks/useParsedMacroParams.ts';
import type { CompleteTaskTrigger } from '../hooks/useCompleteTask.ts';
import { i18n } from '../tasksReportMacroI18n.ts';

import { TaskDescriptionCell, TaskValueCell } from './TaskRow.tsx';

interface TasksReportTableProps {
	tasks: TaskEntity[];
	columns: TasksReportColumn[];
	currentSortBy: string | undefined;
	currentReverseSort: boolean;
	pageSize: number;
	hasNextPage: boolean;
	loadingMore: boolean;
	onSort: (sortKey: string) => void;
	onLoadMore: () => void;
	taskUpdateTrigger?: CompleteTaskTrigger;
	/** Optional callback fired when a task is successfully marked complete. */
	onTaskComplete?: (taskId: string) => Promise<void> | void;
}

const SORTABLE_COLUMNS: TasksReportColumn[] = ['duedate', 'assignee', 'location'];

const COLUMN_LABEL_MESSAGES: Record<
	TasksReportColumn,
	{ id: string; defaultMessage: string; description?: string }
> = {
	description: i18n.columnDescription,
	duedate: i18n.columnDueDate,
	assignee: i18n.columnAssignee,
	location: i18n.columnLocation,
	completedate: i18n.columnCompleteDate,
	labels: i18n.columnLabels,
};

const styles = cssMap({
	tableWrapper: {
		overflowX: 'auto',
		width: '100%',
	},
	paginationControls: {
		display: 'flex',
		justifyContent: 'center',
		gap: token('space.100'),
		marginTop: token('space.100'),
	},
});

function getColumnLabel(
	column: TasksReportColumn,
	formatMessage: (d: { id: string; defaultMessage: string; description?: string }) => string,
): string {
	return formatMessage(COLUMN_LABEL_MESSAGES[column]);
}

function buildTableRow(
	task: TaskEntity,
	columns: TasksReportColumn[],
	serverSortPosition: number | undefined,
	taskUpdateTrigger: CompleteTaskTrigger | undefined,
	onTaskComplete: ((taskId: string) => Promise<void> | void) | undefined,
): RowType {
	const cells: RowType['cells'] = [];

	for (const column of columns) {
		const content =
			column === 'description' ? (
				<TaskDescriptionCell
					task={task}
					taskUpdateTrigger={taskUpdateTrigger}
					onTaskComplete={onTaskComplete}
				/>
			) : (
				<TaskValueCell task={task} column={column} />
			);

		cells.push({
			key: serverSortPosition ?? `${task.id}-${column}`,
			content,
		});
	}

	return {
		key: task.id,
		cells,
	};
}

export const TasksReportTable = ({
	tasks,
	columns,
	currentSortBy,
	currentReverseSort,
	pageSize,
	hasNextPage,
	loadingMore,
	onSort,
	onLoadMore,
	taskUpdateTrigger,
	onTaskComplete,
}: TasksReportTableProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const tableSortOrder: SortOrderType = currentReverseSort ? 'ASC' : 'DESC';
	// Combine currentPage and pendingLoadMore into one state object so all
	// related updates are atomic — avoids chained state setter calls which
	// cause extra re-renders and violate the no-chain-state-updates lint rule.
	const [pageState, setPageState] = useState<{ currentPage: number; pendingLoadMore: boolean }>({
		currentPage: 1,
		pendingLoadMore: false,
	});
	const { currentPage, pendingLoadMore } = pageState;
	const previousLoadingMoreRef = useRef(loadingMore);

	const loadedPages = Math.max(1, Math.ceil(tasks.length / pageSize));
	const effectivePage = Math.min(currentPage, loadedPages);

	// Tasks arrive pre-sorted from the server. Slice the current page from the
	// accumulated list (which grows as the user loads more cursor pages).
	const pageTasks = useMemo(() => {
		const startIndex = (effectivePage - 1) * pageSize;
		return tasks.slice(startIndex, startIndex + pageSize);
	}, [effectivePage, pageSize, tasks]);

	// Reset to page 1 when the user changes sort — the renderer re-queries the
	// server, which resets the task list to the first page of results.
	// Also cancel any in-flight loadMore so the reactive effect doesn't
	// incorrectly advance the page when the new (smaller) task list arrives.
	useEffect(() => {
		setPageState({ currentPage: 1, pendingLoadMore: false });
	}, [currentSortBy, currentReverseSort]);

	useEffect(() => {
		if (tasks.length <= pageSize) {
			setPageState({ currentPage: 1, pendingLoadMore: false });
		}
	}, [tasks, pageSize]);

	const tableHead: HeadType = useMemo(
		() => ({
			cells: columns.map((column) => ({
				key: column,
				content: getColumnLabel(column, formatMessage),
				isSortable: SORTABLE_COLUMNS.includes(column),
			})),
		}),
		[columns, formatMessage],
	);

	const tableRows: RowType[] = useMemo(() => {
		if (expValEquals('cc_tasks_report_preserve_server_sort', 'isEnabled', true)) {
			const sortDirection = tableSortOrder === 'ASC' ? 1 : -1;
			return pageTasks.map((task, index) =>
				buildTableRow(task, columns, sortDirection * index, taskUpdateTrigger, onTaskComplete),
			);
		}
		return pageTasks.map((task) =>
			buildTableRow(task, columns, undefined, taskUpdateTrigger, onTaskComplete),
		);
	}, [columns, onTaskComplete, pageTasks, tableSortOrder, taskUpdateTrigger]);

	const handleTableSort = useCallback(
		({ key }: { key: string | number | undefined }) => {
			if (typeof key !== 'string') {
				return;
			}

			if (!SORTABLE_COLUMNS.includes(key as TasksReportColumn)) {
				return;
			}

			onSort(key);
		},
		[onSort],
	);

	const handlePrevious = useCallback(() => {
		if (currentPage <= 1) {
			return;
		}

		setPageState({ currentPage: currentPage - 1, pendingLoadMore: false });
	}, [currentPage]);

	const handleNext = useCallback(() => {
		if (currentPage < loadedPages) {
			setPageState({ currentPage: currentPage + 1, pendingLoadMore: false });
			return;
		}

		if (hasNextPage && !loadingMore && !pendingLoadMore) {
			// Navigating beyond loaded data — fire the fetch but keep currentPage
			// pointing at the last loaded page. The effect below will advance
			// currentPage once the new data arrives, preventing a blank-table flash.
			setPageState({ currentPage, pendingLoadMore: true });
			onLoadMore();
		}
	}, [currentPage, hasNextPage, loadingMore, loadedPages, onLoadMore, pendingLoadMore]);

	// Reactively advance to the next page once the fetch triggered by onLoadMore()
	// has resolved and tasks.length has grown to cover that page. This avoids the
	// blank-table flash that occurs when currentPage advances eagerly before data
	// is available.
	useEffect(() => {
		if (pendingLoadMore && tasks.length > currentPage * pageSize) {
			setPageState({ currentPage: currentPage + 1, pendingLoadMore: false });
		}
	}, [currentPage, pageSize, pendingLoadMore, tasks.length]);

	useEffect(() => {
		const didFinishLoadingMore = previousLoadingMoreRef.current && !loadingMore;

		if (pendingLoadMore) {
			const didReceiveNewPage = tasks.length > currentPage * pageSize;
			const noMoreResults = !hasNextPage;
			const didNotReceiveNewPage = tasks.length <= currentPage * pageSize;

			if (!didReceiveNewPage && (noMoreResults || (didFinishLoadingMore && didNotReceiveNewPage))) {
				setPageState({ currentPage, pendingLoadMore: false });
			}
		}

		previousLoadingMoreRef.current = loadingMore;
	}, [currentPage, hasNextPage, loadingMore, pageSize, pendingLoadMore, tasks.length]);

	const canGoPrevious = currentPage > 1;
	const canGoNext = currentPage < loadedPages || hasNextPage;
	const isPreviousDisabled = !canGoPrevious;
	const isNextDisabled = !canGoNext || loadingMore || pendingLoadMore;

	return (
		<Box testId="tasks-report-macro">
			<Box xcss={styles.tableWrapper}>
				<DynamicTableStateless
					head={tableHead}
					rows={tableRows}
					onSort={handleTableSort}
					sortKey={currentSortBy}
					sortOrder={tableSortOrder}
					testId="tasks-report-table"
				/>
			</Box>
			{(canGoPrevious || canGoNext) && (
				<Box xcss={styles.paginationControls}>
					<Button
						appearance="subtle"
						testId="tasks-report-prev-button"
						onClick={handlePrevious}
						isDisabled={isPreviousDisabled}
					>
						{formatMessage(i18n.paginationPrevious)}
					</Button>
					<Button
						appearance="subtle"
						testId="tasks-report-next-button"
						onClick={handleNext}
						isDisabled={isNextDisabled}
					>
						{formatMessage(i18n.paginationNext)}
					</Button>
				</Box>
			)}
		</Box>
	);
};
